// src/pages/admin/emails/AdminEmailQueue.tsx
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabaseClient";
import { ENV } from "../../../env";
import QueueItemDialog from "./components/QueueItemDialog";

type QueueStatus = "queued" | "sending" | "sent" | "failed" | "cancelled";

type QueueRow = {
  id: string;
  template_id: string;
  user_id: string;
  status: QueueStatus;
  not_before: string;
  dedupe_key: string;
  payload: any;
  try_count: number;
  last_error: string | null;
  created_at: string;
  updated_at: string;
  email_templates: { id: string; name: string } | null;
  profiles: { id: string; email: string | null; first_name: string | null } | null;
};

type TemplateOpt = { id: string; name: string };

export default function AdminEmailQueue() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [templates, setTemplates] = useState<TemplateOpt[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<QueueStatus | "all">("all");
  const [templateId, setTemplateId] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [detail, setDetail] = useState<QueueRow | null>(null);

  // Laden: Templates für Filter
  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("email_templates").select("id,name").order("name");
      setTemplates(((data ?? []) as TemplateOpt[]));
    })();
  }, []);

  // Laden: Queue-Seite
  async function fetchRows() {
    setLoading(true);
    const from = page * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from("email_queue")
      .select(
        `
        id, template_id, user_id, status, not_before, dedupe_key, payload, try_count, last_error, created_at, updated_at,
        email_templates ( id, name ),
        profiles ( id, email, first_name )
        `,
        { count: "exact" }
      )
      .order("created_at", { ascending: false })
      .range(from, to);

    if (status !== "all") query = query.eq("status", status);
    if (templateId !== "all") query = query.eq("template_id", templateId);

    // Für freie Suche auf E-Mail/Name/Dedupe-Key filtern (clientseitig, simpel)
    const { data, error } = await query;
    if (error) {
      console.error(error);
      setRows([]);
      setLoading(false);
      return;
    }
    let list = (data ?? []) as QueueRow[];
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      list = list.filter((r) => {
        // Korrektur: Zugriff auf das erste Element des Arrays
        const profile = r.profiles;
        const template = r.email_templates;

        const email = profile?.email?.toLowerCase() ?? "";
        const name = template?.name?.toLowerCase() ?? "";
        const fn = profile?.first_name?.toLowerCase() ?? "";

        return (
          email.includes(needle) ||
          name.includes(needle) ||
          fn.includes(needle) ||
          r.dedupe_key.toLowerCase().includes(needle)
        );
      });
    }
    setRows(list);
    setLoading(false);
  }

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, templateId, page, pageSize]);

  const allChecked = useMemo(() => {
    if (rows.length === 0) return false;
    return rows.every((r) => selected.has(r.id));
  }, [rows, selected]);

  function toggleAll() {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  }

  function toggleOne(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }

  // --- Aktionen (sicher) ---

  async function makeDueAndSend(id: string) {
    // 1) Make the item due immediately
    const { error } = await supabase
      .from("email_queue")
      .update({ not_before: new Date().toISOString() })
      .eq("id", id)
      .eq("status", "queued");
    if (error) return alert(error.message);

    // 2) Trigger the sender edge function
    const endpoint = ENV.FUNCTION_ENDPOINT || `${ENV.SUPABASE_URL.replace(/\/+$/, "")}/functions/v1`;
    if (!endpoint) return alert("FUNCTION_ENDPOINT nicht konfiguriert.");
    const { data: sessionData } = await supabase.auth.getSession();
    const jwt = sessionData?.session?.access_token;
    if (!jwt) return alert("Nicht eingeloggt.");

    const res = await fetch(`${endpoint}/email-sender?limit=5`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    const text = await res.text();
    let parsed: any;
    try { parsed = JSON.parse(text); } catch { parsed = { raw: text }; }
    alert(`Sender: ${JSON.stringify(parsed, null, 2)}`);
    await fetchRows();
  }

  async function makeDue(ids: string[]) {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from("email_queue")
      .update({ not_before: new Date().toISOString() })
      .in("id", ids)
      .eq("status", "queued");
    if (error) return alert(error.message);
    await fetchRows();
    setSelected(new Set());
  }

  async function retryFailed(ids: string[]) {
    if (ids.length === 0) return;
    const { error } = await supabase
      .from("email_queue")
      .update({
        status: "queued",
        not_before: new Date().toISOString(),
        try_count: 0,
        last_error: null,
      })
      .in("id", ids)
      .eq("status", "failed");
    if (error) return alert(error.message);
    await fetchRows();
    setSelected(new Set());
  }

  async function cancelQueued(ids: string[]) {
    if (ids.length === 0) return;
    // ESLint-Fehler behoben: Deaktivierung der 'no-restricted-globals' Regel für diese Zeile
    // eslint-disable-next-line no-restricted-globals
    const really = confirm(`Ausgewählte Einträge wirklich abbrechen? (Status → cancelled)`);
    if (!really) return;
    const { error } = await supabase
      .from("email_queue")
      .update({ status: "cancelled" })
      .in("id", ids)
      .in("status", ["queued", "failed"]);
    if (error) return alert(error.message);
    await fetchRows();
    setSelected(new Set());
  }

  return (
    <div className="max-w-7xl mx-auto mt-10 p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-sky-400">📬 Queue</h1>
        <div className="flex items-center gap-3">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Suche (E-Mail, Template, Dedupe-Key)…"
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-100 rounded-lg w-72 outline-none focus:border-sky-400 placeholder:text-slate-600"
          />
          <select
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as any);
              setPage(0);
            }}
          >
            <option value="all">Alle Status</option>
            <option value="queued">queued</option>
            <option value="sending">sending</option>
            <option value="sent">sent</option>
            <option value="failed">failed</option>
            <option value="cancelled">cancelled</option>
          </select>

          <select
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={templateId}
            onChange={(e) => {
              setTemplateId(e.target.value);
              setPage(0);
            }}
          >
            <option value="all">Alle Vorlagen</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>

          <select
            className="px-3 py-2 border border-[#2a3347] bg-[#0d1117] text-slate-200 rounded-lg outline-none focus:border-sky-400"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          <button
            onClick={fetchRows}
            className="px-3 py-2 rounded-lg border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
            title="Neu laden"
          >
            🔄
          </button>

          <Link to="/admin/emails" className="text-sky-400 hover:underline">
            Zurück
          </Link>
        </div>
      </div>

      {/* Bulk-Aktionen */}
      <div className="mb-4 flex items-center gap-2">
        <button
          onClick={() => makeDue(Array.from(selected))}
          className="px-3 py-2 rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 transition-colors"
          disabled={selected.size === 0}
        >
          Fällig machen (queued)
        </button>
        <button
          onClick={() => retryFailed(Array.from(selected))}
          className="px-3 py-2 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] disabled:opacity-50 transition-colors"
          disabled={selected.size === 0}
        >
          Retry (failed → queued)
        </button>
        <button
          onClick={() => cancelQueued(Array.from(selected))}
          className="px-3 py-2 rounded border border-red-800/30 text-red-400 hover:bg-red-950/20 disabled:opacity-50 transition-colors"
          disabled={selected.size === 0}
        >
          Cancel (queued/failed)
        </button>
        <span className="text-sm text-slate-500 ml-2">
          Ausgewählt: {selected.size}
        </span>
      </div>

      {/* Tabelle */}
      {loading ? (
        <div className="p-6 border border-[#2a3347] rounded-xl bg-[#161b27] text-slate-400">Lade…</div>
      ) : rows.length === 0 ? (
        <div className="p-6 border border-[#2a3347] rounded-xl bg-[#161b27] text-slate-500">Keine Einträge.</div>
      ) : (
        <div className="overflow-x-auto border border-[#2a3347] rounded-xl bg-[#161b27]">
          <table className="min-w-full text-sm">
            <thead className="bg-[#1e2639] text-left">
              <tr>
                <th className="px-3 py-3">
                  <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                </th>
                <th className="px-3 py-3 font-semibold text-slate-300">Empfänger</th>
                <th className="px-3 py-3 font-semibold text-slate-300">Vorlage</th>
                <th className="px-3 py-3 font-semibold text-slate-300">Status</th>
                <th className="px-3 py-3 font-semibold text-slate-300">not_before</th>
                <th className="px-3 py-3 font-semibold text-slate-300">try</th>
                <th className="px-3 py-3 font-semibold text-slate-300">dedupe_key</th>
                <th className="px-3 py-3 font-semibold text-slate-300 text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-[#2a3347]">
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-100">{r.profiles?.email ?? "—"}</div>
                    <div className="text-xs text-slate-500">{r.profiles?.first_name ?? "—"}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-300">
                    {r.email_templates?.name ?? r.template_id.slice(0, 8)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} />
                    {r.last_error && (
                      <div className="text-xs text-red-400 mt-1 line-clamp-2 max-w-[280px]">
                        {r.last_error}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-slate-300">
                    <div>{new Date(r.not_before).toLocaleString()}</div>
                    <div className="text-xs text-slate-500">
                      erstellt: {new Date(r.created_at).toLocaleString()}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-slate-300">{r.try_count}</td>
                  <td className="px-3 py-2">
                    <div className="font-mono text-xs break-all max-w-[260px] text-slate-300">
                      {r.dedupe_key}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2 justify-end">
                      <button
                        onClick={() => setDetail(r)}
                        className="px-2 py-1 text-sky-400 hover:underline"
                      >
                        Details
                      </button>
                      {r.status === "queued" && (
                        <button
                          onClick={() => makeDueAndSend(r.id)}
                          className="px-2 py-1 text-slate-300 hover:underline"
                        >
                          Jetzt senden
                        </button>
                      )}
                      {r.status === "failed" && (
                        <button
                          onClick={() => retryFailed([r.id])}
                          className="px-2 py-1 text-slate-300 hover:underline"
                        >
                          Retry
                        </button>
                      )}
                      {(r.status === "queued" || r.status === "failed") && (
                        <button
                          onClick={() => cancelQueued([r.id])}
                          className="px-2 py-1 text-red-400 hover:underline"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-slate-500">Seite {page + 1}</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-2 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] disabled:opacity-50 transition-colors"
            disabled={page === 0}
          >
            ← Zurück
          </button>
          <button
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-2 rounded border border-[#2a3347] bg-[#1e2639] text-slate-300 hover:bg-[#2a3347] transition-colors"
          >
            Weiter →
          </button>
        </div>
      </div>

      {detail && <QueueItemDialog row={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function StatusBadge({ status }: { status: QueueStatus }) {
  const map: Record<QueueStatus, string> = {
    queued: "bg-amber-900/30 text-amber-400",
    sending: "bg-sky-900/30 text-sky-400",
    sent: "bg-emerald-900/30 text-emerald-400",
    failed: "bg-red-900/30 text-red-400",
    cancelled: "bg-[#2a3347] text-slate-400",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs ${map[status]}`}>
      {status}
    </span>
  );
}